import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useAvailabilitySchemeList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refAvailabilitySchemesTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'schemeID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'datePeriod',
      label: 'Applicable Period',
      sortable: true,
    },
    {
      key: 'operatingHours',
      label: 'Operating Hours',
      sortable: true,
    },
    {
      key: 'applicableDays',
      label: 'Applicable days',
      sortable: false,
    },
    {
      key: 'timeSlotDuration',
      label: 'Timeslots Duration',
      sortable: true,
    },
    {
      key: 'quantityPerSlot',
      label: 'Quantity per slot',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalAvailabilitySchemes = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const sortBy = ref(route.query.sortBy ?? 'schemeID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const activityTypeFilter = ref(route.params.id || null)
  const toDeletedID = ref(null)
  let initialLoad = true
  const currentDataLength = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refAvailabilitySchemesTable.value ? refAvailabilitySchemesTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalAvailabilitySchemes.value,
    }
  })

  const refetchData = () => {
    refAvailabilitySchemesTable.value.refresh()
  }

  watch([activityTypeFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchAvailabilitySchemes = (ctx, callback) => {
    store
      .dispatch('app-availability-schemes/fetchAvailabilitySchemes', {
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        activityType: activityTypeFilter.value,
      },
      { root: true })
      .then(response => {
        const { availabilitySchemes, total } = response.data
        currentDataLength.value = availabilitySchemes.length
        callback(availabilitySchemes)
        totalAvailabilitySchemes.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              activityType: activityTypeFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteAvailabilityScheme = () => {
    store
      .dispatch('app-availability-schemes/deleteAvailabilityScheme', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveAvailabilitySchemeStatusVariant = status => {
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  return {
    fetchAvailabilitySchemes,
    tableColumns,
    perPage,
    currentPage,
    totalAvailabilitySchemes,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refAvailabilitySchemesTable,

    resolveAvailabilitySchemeStatusVariant,
    refetchData,
    currentDataLength,

    toDeletedID,
    deleteAvailabilityScheme,
  }
}
